import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Card, CardBody, CardHeader, Button, Label } from "@/Components/ui";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { toast } from "react-hot-toast";
import { ServiceTable, TableGroup, tableOrderService } from "@/api/services/table-order.service";
import { dialogService } from "@/utils/dialogService";

const Tables = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState<ServiceTable[]>([]);
  const [groups, setGroups] = useState<TableGroup[]>([]);
    const [loading, setLoading] = useState(false);
  const [groupFilter, setGroupFilter] = useState("");

    const loadItems = useCallback(async () => {
        setLoading(true);
        try {
      const [groupResponse, tableResponse] = await Promise.all([
        tableOrderService.listAllTableGroups(),
        tableOrderService.listAllServiceTables(),
      ]);
      setGroups(groupResponse.results || []);
      setItems(tableResponse.results || []);
        }
        catch (error) {
            toast.error("테이블 목록을 불러오지 못했습니다.");
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const visibleItems = items.filter((item) => {
      if (!groupFilter)
        return true;
      return String(item.group || "") === groupFilter;
    });

    const handleToggleActive = async (item: ServiceTable) => {
        try {
            await tableOrderService.updateServiceTable(item.id, { is_active: !item.is_active });
            await loadItems();
            toast.success("테이블 상태가 변경되었습니다.");
        }
        catch (error) {
            toast.error("테이블 상태 변경에 실패했습니다.");
        }
    };
    const handleDelete = async (item: ServiceTable) => {
        if (!await dialogService.confirm(`'${item.name}' 테이블을 삭제하시겠습니까?`))
            return;
        try {
            await tableOrderService.deleteServiceTable(item.id);
            await loadItems();
            toast.success("테이블이 삭제되었습니다.");
        }
        catch (error) {
            toast.error("테이블 삭제에 실패했습니다.");
        }
    };
    return (<div className="page-content">
      <Container fluid>
        <BreadCrumb title="테이블 관리" pageTitle="현장주문"/>

        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">테이블 목록</h5>
            <div className="d-flex gap-2">
              <Button color="light" onClick={() => navigate("/table-order/table-groups")}>그룹 관리</Button>
              <Button color="primary" onClick={() => navigate("/table-order/table-form/0")}>추가</Button>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="form-label">테이블 그룹</Label>
                <select className="form-select" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
                  <option value="">전체</option>
                  {groups.map((group) => (<option key={group.id} value={group.id}>{group.name}</option>))}
                </select>
              </div>
            </div>

            {loading ? (<p className="text-muted mb-0">불러오는 중...</p>) : (<div className="w-full overflow-x-auto">
                <table className="w-full mb-0 text-sm border-y border-slate-200">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                    <tr>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">ID</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">그룹</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">코드</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">이름</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">수용 인원</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">정렬</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">상태</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {visibleItems.map((item) => (<tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.id}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.group_name || "미지정"}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.code}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.name}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.capacity}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.display_order}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                          <Button color={item.is_active ? "soft-success" : "soft-secondary"} size="sm" onClick={() => handleToggleActive(item)}>
                            {item.is_active ? "활성" : "비활성"}
                          </Button>
                        </td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                          <Button color="soft-primary" size="sm" className="me-1" onClick={() => navigate(`/table-order/table-form/${item.id}`)}>수정</Button>
                          <Button color="soft-danger" size="sm" onClick={() => handleDelete(item)}>삭제</Button>
                        </td>
                      </tr>))}
                    {visibleItems.length === 0 && (<tr>
                        <td colSpan={8} className="text-center text-muted py-8">데이터가 없습니다.</td>
                      </tr>)}
                  </tbody>
                </table>
              </div>)}
          </CardBody>
        </Card>
      </Container>
    </div>);
};
export default Tables;
